'use client';

import clsx from 'clsx';
import type React from 'react';
import { createContext, useContext, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Link } from './link';

const TableContext = createContext<{
  bleed: boolean;
  dense: boolean;
  compact: boolean;
  grid: boolean;
  striped: boolean;
}>({
  bleed: false,
  dense: false,
  compact: false,
  grid: false,
  striped: false,
});

type TableProps = {
  bleed?: boolean;
  dense?: boolean;
  compact?: boolean;
  grid?: boolean;
  striped?: boolean;
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

type TableHeadProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'thead'>;

type TableBodyProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'tbody'>;

type TableRowProps = {
  href?: string;
  target?: string;
  title?: string;
  className?: string;
  noPointer?: boolean;
} & React.ComponentPropsWithoutRef<'tr'>;

type TableHeaderProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'th'>;

type TableCellProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'td'>;

export function Table({
  bleed = false,
  dense = false,
  compact = false,
  grid = false,
  striped = false,
  className,
  children,
  ...props
}: TableProps) {
  return (
    <TableContext.Provider
      value={{ bleed, dense, compact, grid, striped } as React.ContextType<typeof TableContext>}
    >
      <div className="tw:flow-root">
        <div
          {...props}
          className={clsx(className, 'tw:-mx-[--gutter] tw:overflow-x-auto tw:whitespace-nowrap')}
        >
          <div
            className={clsx(
              'tw:inline-block tw:min-w-full tw:align-middle',
              !bleed && 'tw:sm:px-[--gutter]'
            )}
          >
            <table className="tw:min-w-full tw:text-left tw:text-sm/6 tw:text-zinc-950 tw:dark:text-white">
              {children}
            </table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
}

export function TableHead({ className, ...props }: TableHeadProps) {
  return (
    <thead
      {...props}
      className={clsx(className, 'tw:text-zinc-500  tw:text-xs tw:italic tw:font-light')}
    />
  );
}

export function TableBody({ className, ...props }: TableBodyProps) {
  return <tbody {...props} />;
}

const TableRowContext = createContext<{ href?: string; target?: string; title?: string }>({
  href: undefined,
  target: undefined,
  title: undefined,
});

export function TableRow({
  href,
  target,
  title,
  className,
  noPointer = false,
  ...props
}: TableRowProps) {
  const { striped } = useContext(TableContext);

  return (
    <TableRowContext.Provider
      value={{ href, target, title } as React.ContextType<typeof TableRowContext>}
    >
      <tr
        {...props}
        className={twMerge(
          clsx(
            'tw:hover:bg-brand-green-2',
            !noPointer && 'tw:cursor-pointer',
            href &&
              'tw:has-[[data-row-link][data-focus]]:outline tw:has-[[data-row-link][data-focus]]:outline-2 tw:has-[[data-row-link][data-focus]]:-outline-offset-2 tw:has-[[data-row-link][data-focus]]:outline-brand-green-1 tw:dark:focus-within:bg-white/[2.5%]',
            striped && 'tw:odd:bg-zinc-950/[4%] ',
            // href && striped && 'tw:hover:!bg-brand-green-2 tw:dark:hover:bg-white/5',
            // href && !striped && 'tw:hover:!bg-brand-green-2 tw:dark:hover:bg-white/[2.5%]'
            className
          )
        )}
      />
    </TableRowContext.Provider>
  );
}

export function TableHeader({ className, ...props }: TableHeaderProps) {
  const { bleed, grid, compact } = useContext(TableContext);

  return (
    <th
      {...props}
      className={clsx(
        className,
        'tw:border-b tw:border-b-zinc-950/10 tw:px-4 tw:py-2 tw:font-medium tw:first:pl-[var(--gutter,theme(spacing.2))] tw:last:pr-[var(--gutter,theme(spacing.2))] tw:dark:border-b-white/10',
        grid && 'tw:border-l tw:border-l-zinc-950/5 tw:first:border-l-0 tw:dark:border-l-white/5',
        !bleed && 'tw:sm:first:pl-1 tw:sm:last:pr-1',
        compact && 'tw:py-0.5 tw:px-1'
      )}
    />
  );
}

type TableSortHeaderProps = {
  sortKey: string;
  ordering?: string;
  onOrderingChange: (next: string) => void;
  className?: string;
  children?: React.ReactNode;
} & React.ComponentPropsWithoutRef<'th'>;

// Clickable header with sorting icons. First click on an unsorted column → Ascending.
// eslint-disable-next-line sonarjs/cognitive-complexity
export function TableSortHeader({
  sortKey,
  ordering,
  onOrderingChange,
  className,
  children,
  ...rest
}: TableSortHeaderProps) {
  const isAsc = ordering === sortKey;
  const isDesc = ordering === `-${sortKey}`;
  const ariaSort = isAsc ? 'ascending' : isDesc ? 'descending' : 'none';
  // eslint-disable-next-line sonarjs/no-all-duplicated-branches
  const next = isAsc ? `-${sortKey}` : isDesc ? sortKey : sortKey; // first click → asc

  // Determine icon set (numeric vs alpha) based on header label starting with '#'
  const labelText =
    typeof children === 'string' ? children : typeof children === 'number' ? String(children) : '';
  const isNumericSortLabel = labelText.trim().startsWith('#');

  return (
    <TableHeader
      {...rest}
      aria-sort={ariaSort as React.AriaAttributes['aria-sort']}
      onClick={(e) => {
        rest.onClick?.(e);
        onOrderingChange(next);
      }}
      className={twMerge('tw:cursor-pointer tw:select-none ', className)}
    >
      <span className="tw:inline-flex tw:items-center tw:gap-1">
        {children}
        {isAsc ? (
          <i
            className={`fas ${
              isNumericSortLabel ? 'fa-arrow-down-1-9' : 'fa-arrow-down-a-z'
            } tw:text-brand-green-1`}
          />
        ) : isDesc ? (
          <i
            className={`fas ${
              isNumericSortLabel ? 'fa-arrow-down-9-1' : 'fa-arrow-down-z-a'
            } tw:text-brand-green-1`}
          />
        ) : (
          <i className="fas fa-sort tw:text-zinc-300" />
        )}
      </span>
    </TableHeader>
  );
}

export function TableCell({ className, children, ...props }: TableCellProps) {
  const { bleed, dense, compact, grid, striped } = useContext(TableContext);
  const { href, target, title } = useContext(TableRowContext);
  const [cellRef, setCellRef] = useState<HTMLElement | null>(null);

  return (
    <td
      ref={href ? setCellRef : undefined}
      {...props}
      className={twMerge(
        clsx(
          className,
          'tw:relative tw:px-4 tw:first:pl-[var(--gutter,theme(spacing.2))] tw:last:pr-[var(--gutter,theme(spacing.2))]',
          !striped && 'tw:border-b tw:border-zinc-950/5 tw:dark:border-white/5',
          grid && 'tw:border-l tw:border-l-zinc-950/5 tw:first:border-l-0 tw:dark:border-l-white/5',
          dense ? 'tw:py-2.5' : 'tw:py-4',
          compact && 'tw:py-0.5 tw:px-1',
          !bleed && 'tw:sm:first:pl-1 tw:sm:last:pr-1'
        )
      )}
    >
      {href && (
        <Link
          data-row-link
          href={href}
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="tw:absolute tw:inset-0 tw:focus:outline-none"
        >
          <span aria-hidden="true" />
        </Link>
      )}
      {children}
    </td>
  );
}
